import { useRoles } from "@/hooks/useRoles";
import { Loading } from "@/components/Page/Loading";
import { Forbidden } from "@/components/Page/Forbidden";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { useNavigate, useParams } from "react-router-dom";
import { useShopQuery } from "@/hooks/queries/useShopQuery";
import { ShopForm } from "@/components/Shops/Shop.form";
import { useUpdateShopMutation } from "@/hooks/mutations/useUpdateShopMutation";
import { Shop, SyncDriver } from "@/types/v2";
import { useEffect, useState } from "react";
import { sentry } from "@/services/sentry";
import { toast } from "@/hooks/useToast";
import { firestore } from "@/services/firestore";
import { httpsCallables } from "@/repositories/httpsCallables";
import { Button } from "@/components/Base/Button";
import { ReassignOwnerDialog } from "@/components/Shops/ReassignOwner.dialog";
import { withoutOwnershipFields } from "@/lib/shopOwnership";
import { safeFirestoreToDateTime } from "@/lib/utils";
import {
  hasLiveSyncClaim,
  isRequestQueued,
  isSyncOngoing,
  isSyncStale,
  PendingSyncRequest,
} from "@/lib/syncDriverStatus";

/**
 * Staleness is time-based and a dead sync stops writing its driver document, so
 * the page must re-evaluate on a clock rather than only on Firestore snapshots.
 * 30s costs at most half a minute of lag against a 10-minute threshold.
 */
const TICK_MS = 30 * 1000;

const ShopPage = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin, isInitialized } = useRoles();
  useIsSuperAdmin();
  const { shopId } = useParams();
  const { data, isLoading } = useShopQuery(shopId ?? "");
  const updateShopMutation = useUpdateShopMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingLoginEmail, setIsSendingLoginEmail] = useState(false);
  const [pendingRequest, setPendingRequest] =
    useState<PendingSyncRequest | null>(null);
  const [syncDriver, setSyncDriver] = useState<SyncDriver | undefined>(
    undefined
  );
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const lastSyncAttemptAtMs = safeFirestoreToDateTime(
    syncDriver?.lastSyncAttemptAt
  )?.getTime();
  // Narrow: an invocation holds the claim. Drives `disabled`, and staleness.
  const isSyncing = hasLiveSyncClaim(syncDriver);
  // Widened: a run is underway, gaps between pages included. LABEL ONLY --
  // widening `disabled` too would leave the admin with no usable button after a
  // setSuperseded, and the double-click it would prevent is already absorbed by
  // createOrSkip's isBusy check.
  const isOngoing = isSyncOngoing(syncDriver);
  const isStuck = isSyncStale(syncDriver, lastSyncAttemptAtMs, now.getTime());
  const queued = isRequestQueued(
    pendingRequest,
    lastSyncAttemptAtMs,
    now.getTime()
  );

  useEffect(() => {
    if (!shopId) return;
    const unsubscribe = firestore.subscribeDocument<SyncDriver>(
      `shops/${shopId}/sync/syncdriver`,
      (doc) => {
        setSyncDriver(doc);
      }
    );
    return unsubscribe;
  }, [shopId]);

  const handleCancel = () => {
    navigate("/shops");
  };

  // Shared by both handlers below. The queue can refuse a request outright
  // (disabled shop, unknown shop, unauthenticated). Nothing used to read
  // this, so the button sat on "Queued..."/"Forcing..." with no explanation.
  // `actionName` parameterizes only the destructive toast's title so it
  // still reads "Full Re-Sync was not queued" / "Force Sync was not queued".
  const handleSyncRefusal = (
    actionName: string,
    data: { queued: boolean; reason?: string } | undefined
  ) => {
    if (data?.queued !== false) return;
    setPendingRequest(null);
    // Two shapes of refusal, and they must not look alike. A `reason` means
    // a genuine problem (disabled shop, unknown shop, unauthenticated). No
    // reason means createOrSkip skipped a busy shop -- which is exactly what
    // happens when an admin clicks the enabled "Syncing..." button during a
    // page gap. That is the system working, so it gets an informational
    // toast, not a destructive one.
    //
    // The no-reason branch is reachable only via handleFullReSync's
    // (non-forced) call. For Force Sync it is structurally unreachable:
    // createOrSkip's busy-shop skip is gated on `!options?.force && doc.exists`
    // (integrations/src/repositories/syncRequest.ts), so `force: true` always
    // bypasses that check, and requestSync's separate rate-limit guard only
    // applies to `syncType === "incremental"` and is itself disabled by
    // `force`. So a Force Sync `queued: false` can only come from
    // onCallRequestSync's early returns, every one of which carries a
    // `reason`. The branch stays here as shared code -- live for Full
    // Re-Sync, harmless (never taken) for Force Sync.
    toast(
      data.reason
        ? {
            title: `${actionName} was not queued`,
            description: data.reason,
            variant: "destructive",
          }
        : {
            title: "Already syncing",
            description: "A sync is already in progress for this shop.",
          }
    );
  };

  const handleFullReSync = () => {
    if (!shopId) return;
    setPendingRequest({
      clickedAtMs: Date.now(),
      driverStampMs: lastSyncAttemptAtMs,
    });
    httpsCallables
      .requestSyncWithSource({ shopId, syncType: "fullReSync" })
      .then((result) => {
        handleSyncRefusal("Full Re-Sync", result?.data);
      })
      .catch((error: unknown) => {
        sentry.captureException(error);
        setPendingRequest(null);
        toast({
          title: "Full Re-Sync failed",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          variant: "destructive",
        });
      });
  };

  const handleForceSync = () => {
    if (!shopId) return;
    setPendingRequest({
      clickedAtMs: Date.now(),
      driverStampMs: lastSyncAttemptAtMs,
    });
    httpsCallables
      .requestSyncWithSource({ shopId, syncType: "fullReSync", force: true })
      .then((result) => {
        handleSyncRefusal("Force Sync", result?.data);
      })
      .catch((error: unknown) => {
        sentry.captureException(error);
        setPendingRequest(null);
        toast({
          title: "Force Sync failed",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          variant: "destructive",
        });
      });
  };

  const handleSendLoginEmail = async () => {
    if (!data || !shopId) return;
    setIsSendingLoginEmail(true);
    try {
      const result = await httpsCallables.adminSendLoginEmail({
        email: data.email,
        displayName: `${data.name} Owner`,
        shopId,
        employeeId: shopId,
        loginLink: `${window.location.origin}/login`,
      });

      toast(
        result.data.emailSent
          ? { title: "Login email sent!", variant: "success" }
          : {
              title:
                "Account updated, but we couldn't confirm the email sent. Check with the owner, or try again.",
            }
      );
    } catch (error) {
      sentry.captureException(error);
      toast({
        title:
          "Unable to send the login email. Try again in a few minutes or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSendingLoginEmail(false);
    }
  };

  const handleUpdateShop = (shopId: string, updates: Partial<Shop>) => {
    setIsSubmitting(true);
    updateShopMutation.mutate(
      { shopId, shopData: updates },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          toast({
            title: "Shop updated successfully",
            description: "The shop details have been updated.",
            variant: "success",
          });
        },
        onError: (error) => {
          setIsSubmitting(false);
          sentry.captureException(error);
          toast({
            title: "Error updating shop",
            description:
              error instanceof Error
                ? error.message
                : "An unknown error occurred",
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleSubmit = (data: Partial<Shop>) => {
    if (!shopId) return;
    // ShopForm submits its whole (possibly stale) initialData; ownership
    // fields are owned by the Reassign owner flow and must never be
    // written back from here.
    handleUpdateShop(shopId, withoutOwnershipFields(data));
  };

  if (!isInitialized || isLoading || !data) return <Loading />;
  if (isInitialized && !(isSuperAdmin || isAdmin)) return <Forbidden />;

  // The form seeds its state from initialData once, at mount. After a
  // reassignment the refetched shop has new ownership values, so remount the
  // form when (and only when) they change.
  const ownershipKey = [
    data.email,
    data.ownerId ?? "",
    (data.adminUserIds ?? []).join(","),
  ].join("|");

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-row items-center justify-between gap-2">
        <h1 className="text-2xl">{data?.name} Details</h1>
        <div className="flex items-center gap-3">
          {isSuperAdmin && (
            <>
              <ReassignOwnerDialog
                shopId={shopId ?? ""}
                shopName={data.name}
                currentOwnerEmail={data.email}
              />
              <Button
                variant="outline"
                disabled={isSendingLoginEmail}
                onClick={handleSendLoginEmail}
              >
                {isSendingLoginEmail ? "Sending..." : "Send login email"}
              </Button>
            </>
          )}
          {data?.integration !== "manual" && (
            <div className="flex items-center gap-3">
              {isStuck && (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium">Force Sync</p>
                    <p className="text-xs text-muted-foreground">
                      Override the stuck sync and start fresh.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    disabled={queued}
                    onClick={handleForceSync}
                  >
                    {queued ? "Forcing..." : "Force Sync"}
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium">Full Re-Sync</p>
                  <p className="text-xs text-muted-foreground">
                    Forces all cards to their label/tech containers, overriding
                    manual moves.
                  </p>
                </div>
                <Button
                  variant="outline"
                  disabled={queued || isSyncing}
                  onClick={handleFullReSync}
                >
                  {queued
                    ? "Queued..."
                    : isOngoing
                      ? "Syncing..."
                      : "Full Re-Sync"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="overflow-auto">
        <div className="mx-auto max-w-screen-lg">
          <ShopForm
            key={ownershipKey}
            isSubmitting={isSubmitting}
            initialData={data}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            cancelTitle={"Back to Shops"}
            submitTitle={"Update Shop"}
          />
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
